import type { Ring, Vec2, Vec3 } from '../../../geo/types';
import type { Tower } from '../../../data/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { meshRectWall, type Hole } from '../wallMesh';
import { horizontalCap, orientedFace, planPoint } from '../solids';
import { WORLD } from '../../../core/config';

/** Small defensive openings, cut into the masonry rather than laid over its outer face.
 * Their exact spacing and depth are estimates: the painting establishes the rows of openings,
 * base courses and eave ledges, but cannot survey every unseen side of a tower.
 */
const MASONRY = { bodyScale: 0.98, foot: 0.6, cornice: 0.24, reveal: 0.28 } as const;
/** Continuous masonry below the visible waterline; its buried depth is an estimate. */
export const TOWER_FOUNDATION_Y = WORLD.waterY - 0.8;

function scaled(ring: Ring, scale: number): Ring {
  return ring.map((p): Vec2 => [p[0] * scale, p[1] * scale]);
}

function band(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  ring: Ring,
  from: number,
  to: number,
  y0: number,
  y1: number
): void {
  const lower = scaled(ring, from);
  const upper = scaled(ring, to);
  for (let i = 0; i < ring.length; i++) {
    const a = lower[i]!;
    const b = lower[(i + 1) % ring.length]!;
    const c = upper[(i + 1) % ring.length]!;
    const d = upper[i]!;
    orientedFace(
      builder,
      style,
      frame,
      [
        planPoint(a[0], y0, a[1]),
        planPoint(b[0], y0, b[1]),
        planPoint(c[0], y1, c[1]),
        planPoint(d[0], y1, d[1]),
      ],
      [b[1] - a[1], 0, b[0] - a[0]]
    );
  }
}

function holes(tower: Tower, edge: number, count: number, width: number, height: number): Hole[] {
  // Leave the half-round tower's city-side back plain, where the wall-walk joins it.
  if (tower.shape === 'half-round' && edge === count - 1) return [];
  const gunTower = tower.id === 'tower-rondeel';
  const step = gunTower ? 2 : tower.shape === 'square' ? 1 : tower.shape === 'octagonal' ? 2 : 4;
  if (edge % step !== Math.floor(step / 2)) return [];
  const lower = Math.min(2.0, height * 0.33);
  const slitWidth = Math.min(
    width * (gunTower ? 0.5 : 0.3),
    gunTower ? 0.75 : tower.radius >= 5 ? 0.4 : 0.28
  );
  const out: Hole[] = [
    {
      x: (width - slitWidth) / 2,
      y: lower,
      width: slitWidth,
      height: gunTower ? 0.5 : 0.9,
      cuts: [],
    },
  ];
  if (height > 6.2) {
    const upperWidth = Math.min(width * 0.35, tower.radius >= 5 ? 0.55 : 0.32);
    out.push({
      x: (width - upperWidth) / 2,
      y: height - 2.0,
      width: upperWidth,
      height: 1.05,
      cuts: [],
    });
  }
  return out;
}

/** Rectangular defensive wall with actual recessed openings; reused by small gate/blockhouse bodies. */
export function defensiveMasonryFace(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  a: Vec2,
  b: Vec2,
  foot: number,
  height: number,
  openings: readonly Hole[],
  nicheStyle?: VertexStyle
): void {
  const width = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const along: Vec2 = [(b[0] - a[0]) / width, (b[1] - a[1]) / width];
  const outward: Vec3 = [along[1], 0, along[0]];
  const point = (x: number, y: number, depth = 0): Vec3 =>
    planPoint(
      a[0] + along[0] * x - along[1] * depth,
      foot + y,
      a[1] + along[1] * x + along[0] * depth
    );
  const mesh = meshRectWall(width, height, openings);
  for (const fan of mesh.wall)
    orientedFace(
      builder,
      style,
      frame,
      fan.map(([x, y]) => point(x, y)),
      outward
    );
  const dark: VertexStyle = nicheStyle ?? {
    ...style,
    surface: SURFACE.stone,
    color: style.color.map((c) => c * 0.28) as [number, number, number],
  };
  for (const pane of mesh.panes) {
    const { x0, x1, y0, y1 } = pane;
    const front = [point(x0, y0), point(x1, y0), point(x1, y1), point(x0, y1)];
    const back = [
      point(x0, y0, MASONRY.reveal),
      point(x1, y0, MASONRY.reveal),
      point(x1, y1, MASONRY.reveal),
      point(x0, y1, MASONRY.reveal),
    ];
    orientedFace(builder, dark, frame, back, outward);
    orientedFace(builder, style, frame, [front[0]!, back[0]!, back[1]!, front[1]!], [0, 1, 0]);
    orientedFace(builder, style, frame, [front[3]!, front[2]!, back[2]!, back[3]!], [0, -1, 0]);
    orientedFace(
      builder,
      style,
      frame,
      [front[0]!, front[3]!, back[3]!, back[0]!],
      [along[0], 0, -along[1]]
    );
    orientedFace(
      builder,
      style,
      frame,
      [front[1]!, back[1]!, back[2]!, front[2]!],
      [-along[0], 0, along[1]]
    );
  }
}

/** The connected tower shell, within its existing obstacle outline. The battered foot and eave
 * course meet the recessed body edge to edge, so they neither overlap nor add floating panels.
 */
export function buildTowerMasonry(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  tower: Tower,
  ring: Ring,
  height: number,
  top: boolean,
  nicheStyle?: VertexStyle
): void {
  const body = scaled(ring, MASONRY.bodyScale);
  const upper = height - MASONRY.cornice;
  band(builder, style, frame, ring, 1, 1, TOWER_FOUNDATION_Y, MASONRY.foot * 0.75);
  band(builder, style, frame, ring, 1, MASONRY.bodyScale, MASONRY.foot * 0.75, MASONRY.foot);
  for (let i = 0; i < body.length; i++) {
    const a = body[i]!;
    const b = body[(i + 1) % body.length]!;
    const width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const h = upper - MASONRY.foot;
    defensiveMasonryFace(
      builder,
      style,
      frame,
      a,
      b,
      MASONRY.foot,
      h,
      holes(tower, i, body.length, width, h),
      nicheStyle
    );
  }
  band(builder, style, frame, ring, MASONRY.bodyScale, 1, upper, height);
  if (top) horizontalCap(builder, style, frame, ring, height, true);
}
